/**
 * Shared building blocks for the Locations pages, built from the component library and
 * following the Forms / TaskManagement patterns (clickable stat tiles, titled panels, colour rails).
 */

import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconAlertCircle, IconArrowLeft, IconLock } from '@tabler/icons-react';
import { Alert, Badge, Box, Button, Card, Group, SimpleGrid, Skeleton, Stack, Text, ThemeIcon, Title, Tooltip } from '@/components/ui';
import { Avatar, Divider } from '@/components/ui/data-display';
import { colorOrNull, errorMessage, initials, locationStatus, STATUS_COLORS } from './locationsUtils';

// ---------------------------------------------------------------- page header

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  backTo?: string;
  backLabel?: string;
}

export function PageHeader({ title, description, actions, backTo, backLabel = 'Back' }: PageHeaderProps) {
  return (
    <Stack gap="xs">
      {backTo && (
        <Group>
          <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />} component={Link} to={backTo}>
            {backLabel}
          </Button>
        </Group>
      )}
      <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Title order={2}>{title}</Title>
          {description && (
            <Text c="dimmed" size="sm">
              {description}
            </Text>
          )}
        </Stack>
        {actions && (
          <Group gap="sm" wrap="wrap">
            {actions}
          </Group>
        )}
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------- stat tile

interface StatTileProps {
  icon: ReactNode;
  color: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  active?: boolean;
  /** Coloured left rail for things that need attention. */
  emphasis?: boolean;
  onClick?: () => void;
}

export function StatTile({ icon, color, label, value, hint, active, emphasis, onClick }: StatTileProps) {
  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        backgroundColor: active ? `var(--mantine-color-${color}-light)` : undefined,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {(active || emphasis) && (
        <Box style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: `var(--mantine-color-${color}-6)` }} />
      )}
      <Group wrap="nowrap" gap="md" align="center">
        <ThemeIcon size={44} radius="md" variant={active ? 'filled' : 'light'} color={color} style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={1}>
            {label}
          </Text>
          <Text size="xl" fw={700} lh={1.2}>
            {value}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {hint}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

export function StatTilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: count }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={86} radius="md" />
      ))}
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------- section card

interface SectionCardProps {
  icon?: ReactNode;
  color?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  noDivider?: boolean;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  style?: CSSProperties;
}

export function SectionCard({ icon, color = 'blue', title, description, actions, children, noDivider, padding = 'lg', style }: SectionCardProps) {
  return (
    <Card shadow="sm" padding={padding} radius="md" withBorder style={style}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb={noDivider ? 'sm' : 'xs'}>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
          {icon && (
            <ThemeIcon size="lg" radius="md" variant="light" color={color} style={{ flexShrink: 0 }}>
              {icon}
            </ThemeIcon>
          )}
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Title order={4}>{title}</Title>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Group>
        {actions && (
          <Group gap="xs" wrap="wrap">
            {actions}
          </Group>
        )}
      </Group>
      {!noDivider && <Divider mb="md" />}
      {children}
    </Card>
  );
}

// ---------------------------------------------------------------- colour + status

export function ColorSwatch({ color, size = 14, title }: { color?: string | null; size?: number; title?: string }) {
  const hex = colorOrNull(color);
  return (
    // Native span: the themed Box drops DOM attributes such as title / aria-hidden.
    <span
      aria-hidden
      title={title ?? (hex ? hex : 'No colour')}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        backgroundColor: hex ?? 'transparent',
        border: hex ? '1px solid rgba(0,0,0,0.12)' : '1px dashed var(--mantine-color-dimmed)',
      }}
    />
  );
}

export function LocationStatusBadge({ location, size = 'sm' }: { location: { isActive: boolean; isDeleted?: boolean | null }; size?: 'xs' | 'sm' | 'md' }) {
  const status = locationStatus(location);
  return (
    <Badge size={size} variant="light" color={STATUS_COLORS[status]} styles={{ root: { textTransform: 'none' } }}>
      {status}
    </Badge>
  );
}

/** Small neutral or coloured badge with normal casing. */
export function Tag({ color = 'gray', children, icon, tooltip, variant = 'light' }: { color?: string; children: ReactNode; icon?: ReactNode; tooltip?: string; variant?: 'light' | 'outline' | 'filled' | 'dot' }) {
  const badge = (
    <Badge size="sm" variant={variant} color={color} leftSection={icon} styles={{ root: { textTransform: 'none' } }}>
      {children}
    </Badge>
  );
  return tooltip ? (
    <Tooltip label={tooltip} withArrow multiline maw={280}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
}

// ---------------------------------------------------------------- feedback

/** Inline failure: the server message (409 text / 400 field messages) under a short title. */
export function ErrorNotice({ error, title = 'That did not work', onRetry }: { error: unknown; title?: string; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <Alert color="red" icon={<IconAlertCircle size={16} />} title={title} action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}>
      {errorMessage(error)}
    </Alert>
  );
}

/** Shown in place of a section the user has no right to see. */
export function LockedNotice({ title = 'No access', children }: { title?: string; children: ReactNode }) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="gray" size="lg" radius="md">
          <IconLock size={18} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed">
            {children}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
}

// ---------------------------------------------------------------- people

interface PersonCellProps {
  name: string;
  subtitle?: ReactNode;
  avatar?: string | null;
  muted?: boolean;
  size?: 'sm' | 'md';
}

export function PersonCell({ name, subtitle, avatar, muted, size = 'sm' }: PersonCellProps) {
  return (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <Avatar src={avatar || undefined} radius="xl" size={size === 'sm' ? 32 : 40} color="blue">
        {initials(name)}
      </Avatar>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} c={muted ? 'dimmed' : undefined} lineClamp={1}>
          {name}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {subtitle}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

/** Label / value pair for detail cards. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Box style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{children}</Box>
    </Stack>
  );
}
